import { useState } from "react";
import { toast } from "sonner";
import { Bell, FlaskConical, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [prefs, setPrefs] = useState(user?.notif_prefs || { wins: true, gifts: true });
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState(!!user?.research_panel);
  const [panelSaving, setPanelSaving] = useState(false);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try { await api.put("/profile/notif-prefs", { [key]: next[key] }); await checkAuth?.(); }
    catch (e) { toast.error("Couldn't save"); setPrefs(prefs); }
    finally { setSaving(false); }
  };

  const togglePanel = async () => {
    const next = !panel;
    setPanel(next);
    setPanelSaving(true);
    try {
      await api.post("/me/research-panel", { opt_in: next });
      await checkAuth?.();
      toast.success(next ? "You're on the research panel — thanks!" : "You've left the research panel.");
    } catch (e) { toast.error("Couldn't save"); setPanel(!next); }
    finally { setPanelSaving(false); }
  };

  const ROWS = [
    { key: "wins", label: "Roast picked as winner", desc: "When a submitter selects your roast and you get paid." },
    { key: "gifts", label: "Someone roasted you (a gift)", desc: "When a gift roast for you is revealed." },
  ];

  return (
    <div className="max-w-lg">
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Settings</h1>
      <p className="text-zinc-400 mt-2 mb-8">Control what pings you. We only ever notify you about real events — never re-engagement nudges.</p>

      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5">
        <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4"><Bell size={14} className="text-brand" /> Notifications</div>
        <div className="space-y-3">
          {ROWS.map((r) => (
            <label key={r.key} className="flex items-center justify-between gap-4 rounded-sm border border-white/10 p-3 cursor-pointer hover:border-white/25 transition-colors">
              <div>
                <div className="text-sm font-semibold text-zinc-100">{r.label}</div>
                <div className="text-xs text-zinc-500">{r.desc}</div>
              </div>
              <input data-testid={`notif-${r.key}`} type="checkbox" checked={!!prefs[r.key]} disabled={saving} onChange={() => toggle(r.key)} className="accent-[#ff4500] w-5 h-5 shrink-0" />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mt-6" data-testid="research-panel-section">
        <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4"><FlaskConical size={14} className="text-brand" /> Research panel</div>
        <label className="flex items-start justify-between gap-4 rounded-sm border border-white/10 p-3 cursor-pointer hover:border-white/25 transition-colors">
          <div>
            <div className="text-sm font-semibold text-zinc-100">Join the RoastLit research panel</div>
            <div className="text-xs text-zinc-500 mt-1">Opt in to occasionally help brands with fun product/ad decisions. Your votes are only ever shared as <strong>anonymous, aggregated</strong> results — never your name, profile, or individual answers. Separate from normal app use. You can leave any time.</div>
          </div>
          <input data-testid="research-panel-toggle" type="checkbox" checked={panel} disabled={panelSaving} onChange={togglePanel} className="accent-[#ff4500] w-5 h-5 shrink-0 mt-0.5" />
        </label>
        {panel && (
          <div className="flex items-center gap-2 text-xs text-tier-mild mt-3" data-testid="research-panel-on">
            <ShieldCheck size={14} /> You're in. Aggregated results only — we never reveal who you are.
          </div>
        )}
      </div>
    </div>
  );
}
